// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import LoadingSpinner, { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import importPrivateLists from "@keller/App/Handlers/WishLists/ImportPrivateLists";
import { xScreen } from "@keller/XDL/Utilities";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => {
    return {
        previousEmailAddresses: getSession(state).properties?.previousEmailAddresses,
    };
};

const mapDispatchToProps = {
    importPrivateLists,
};

type Props = WidgetProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface MyListsImportButtonStyles {
    wrapper?: InjectableCss;
    importListButton?: ButtonPresentationProps;
    spinner?: LoadingSpinnerProps;
}

export const importListButtonStyles: MyListsImportButtonStyles = {
    wrapper: {
        css: css`
            display: flex;
            width: 100%;
            ${({ buttonAlignment }: any) =>
                buttonAlignment &&
                css`
                    justify-content: ${buttonAlignment};
                `}
        `,
    },
    importListButton: {
        variant: "secondary",
        css: css`
            ${xScreen("lte", "xs")} {
                width: 100%;
            }
        `,
    },
    spinner: {
        size: 12,
        css: css`
            margin-right: 8px;
        `,
    },
};

const styles = importListButtonStyles;

const MyListsImportListButton: React.FC<Props> = ({ fields, importPrivateLists, previousEmailAddresses }) => {
    const [isLoading, setIsLoading] = React.useState(false);
    const toasterContext = React.useContext(ToasterContext);

    if (!previousEmailAddresses) {
        return null;
    }

    const importListsClickHandler = () => {
        setIsLoading(true);
        importPrivateLists({
            onSuccess: result => {
                setIsLoading(false);
                toasterContext.addToast({
                    body: result.message,
                    messageType: result.importedListsCount === 0 ? "warning" : "success",
                    timeoutLength: 2500,
                });
            },
            onError: error => {
                setIsLoading(false);
                toasterContext.addToast({
                    body: error,
                    messageType: "danger",
                });
            },
        });
    };

    return (
        <StyledWrapper {...styles.wrapper} {...{ buttonAlignment: fields.buttonAlignment }}>
            <Button
                {...styles.importListButton}
                onClick={importListsClickHandler}
                data-test-selector="myLists_import_privateLists"
                disabled={isLoading}
            >
                {isLoading && <LoadingSpinner {...styles.spinner} />}
                {siteMessage("Custom_Import_PrivateLists", previousEmailAddresses)}
            </Button>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(MyListsImportListButton),
    definition: {
        group: "My Lists",
        displayName: "Import Lists Button",
        allowedContexts: ["MyListsPage"],
        fieldDefinitions: [
            {
                name: "buttonAlignment",
                editorTemplate: "DropDownField",
                defaultValue: "flex-end",
                fieldType: "Contextual",
                options: [
                    {
                        displayName: "Start",
                        value: "flex-start",
                    },
                    {
                        displayName: "End",
                        value: "flex-end",
                    },
                    {
                        displayName: "Center",
                        value: "center",
                    },
                ],
            },
        ],
    },
};

export default widgetModule;
